(() => {
  "use strict";

  if (window.SupplyMeFreeOrder) return;

  const root = document.querySelector("[data-supplyme-free-order]");
  if (!root) return;

  document.documentElement.classList.add("supplyme-free-order-installed");
  window.setTimeout(() => {
    if (!window.SupplyMeFreeOrder) {
      document.documentElement.classList.remove("supplyme-free-order-installed");
    }
  }, 5000);

  const SELECTORS = {
    checkout: 'button[name="checkout"]',
    drawerButtons: ".cart-drawer__checkout-buttons",
    dynamicButtons: ".dynamic-cart-btns"
  };
  const STORAGE_PREFIX = "supplyme_free_order_idempotency_v1:";
  const CART_EVENTS = [
    "on:cart:add",
    "on:line-item:change",
    "supplyme:physical-bonus:cart-changed",
    "on:bfcache:load-restore"
  ];
  const state = {
    timer: 0,
    sequence: 0,
    submitting: false,
    lastFingerprint: "",
    lastContext: null
  };

  function routesRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  async function readJson(response) {
    const text = await response.text();
    try { return text ? JSON.parse(text) : {}; } catch { return {}; }
  }

  function normalizeCart(cart) {
    return {
      token: String(cart?.token || ""),
      currency: String(cart?.currency || "").toUpperCase(),
      items: (cart?.items || []).map((item) => ({
        key: String(item.key || ""),
        product_id: String(item.product_id || ""),
        variant_id: String(item.variant_id || item.id || ""),
        quantity: Number(item.quantity || 0),
        original_price_cents: Number(item.original_price || 0),
        final_price_cents: Number(item.final_price || 0),
        selling_plan_id: item.selling_plan_allocation?.selling_plan?.id || null
      }))
    };
  }

  function fingerprint(cart) {
    return JSON.stringify({
      token: cart.token,
      currency: cart.currency,
      items: cart.items.map((item) => ({
        key: item.key,
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        original_price_cents: item.original_price_cents,
        final_price_cents: item.final_price_cents,
        selling_plan_id: item.selling_plan_id
      })).sort((a, b) => a.key.localeCompare(b.key))
    });
  }

  async function loadCart() {
    const response = await fetch(`${routesRoot()}cart.js`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store"
    });
    if (!response.ok) throw new Error("Unable to read cart.");
    return normalizeCart(await response.json());
  }

  function actionFor(checkout) {
    const container = checkout.closest(SELECTORS.drawerButtons) || checkout.parentElement;
    if (!container) return null;

    let action = container.querySelector(":scope > [data-supplyme-free-order-action]");
    if (!action) {
      action = document.createElement("div");
      action.className = "supplyme-free-order-action";
      action.dataset.supplymeFreeOrderAction = "";
      action.hidden = true;
      action.innerHTML = `
        <button type="button" class="btn btn--primary btn--icon-with-text w-full supplyme-free-order-action__button" data-supplyme-free-order-submit></button>
        <p class="supplyme-free-order-action__helper" data-supplyme-free-order-helper></p>
        <p class="supplyme-free-order-action__status" data-supplyme-free-order-status role="alert" aria-live="polite" hidden></p>
      `;
      checkout.insertAdjacentElement("afterend", action);
      action.querySelector("[data-supplyme-free-order-submit]")?.addEventListener("click", submitOrder);
    }

    checkout.classList.add("supplyme-free-order-original");
    const button = action.querySelector("[data-supplyme-free-order-submit]");
    const helper = action.querySelector("[data-supplyme-free-order-helper]");
    const buttonLabel = root.dataset.buttonLabel || "Get My Free Printables";
    const helperText = root.dataset.helperText || "";
    if (!button.textContent) button.textContent = buttonLabel;
    if (helper.textContent !== helperText) helper.textContent = helperText;
    return action;
  }

  function checkoutGroups() {
    return [...document.querySelectorAll(SELECTORS.checkout)].map((checkout) => ({
      checkout,
      action: actionFor(checkout),
      dynamic: checkout.closest(SELECTORS.drawerButtons)?.querySelector(SELECTORS.dynamicButtons) ||
        checkout.parentElement?.parentElement?.querySelector(SELECTORS.dynamicButtons)
    })).filter((group) => group.action);
  }

  function showCheckout() {
    checkoutGroups().forEach(({ checkout, action, dynamic }) => {
      checkout.classList.add("supplyme-free-order-checkout-released");
      if (checkout.hidden) checkout.hidden = false;
      if (!action.hidden) action.hidden = true;
      if (dynamic) {
        if (dynamic.hidden) dynamic.hidden = false;
        dynamic.classList.remove("supplyme-free-order-dynamic");
      }
    });
  }

  function showChecking() {
    checkoutGroups().forEach(({ checkout, action, dynamic }) => {
      checkout.classList.remove("supplyme-free-order-checkout-released");
      if (!checkout.hidden) checkout.hidden = true;
      if (action.hidden) action.hidden = false;
      if (dynamic) {
        if (!dynamic.hidden) dynamic.hidden = true;
        dynamic.classList.add("supplyme-free-order-dynamic");
      }
      const button = action.querySelector("[data-supplyme-free-order-submit]");
      const helper = action.querySelector("[data-supplyme-free-order-helper]");
      const status = action.querySelector("[data-supplyme-free-order-status]");
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = root.dataset.checkingLabel || "Checking Cart…";
      helper.hidden = true;
      status.hidden = true;
      status.textContent = "";
    });
  }

  function showFreeOrder() {
    checkoutGroups().forEach(({ checkout, action, dynamic }) => {
      checkout.classList.remove("supplyme-free-order-checkout-released");
      if (!checkout.hidden) checkout.hidden = true;
      if (action.hidden) action.hidden = false;
      if (dynamic) {
        if (!dynamic.hidden) dynamic.hidden = true;
        dynamic.classList.add("supplyme-free-order-dynamic");
      }
      const status = action.querySelector("[data-supplyme-free-order-status]");
      const button = action.querySelector("[data-supplyme-free-order-submit]");
      const helper = action.querySelector("[data-supplyme-free-order-helper]");
      button.disabled = false;
      button.setAttribute("aria-busy", "false");
      button.textContent = root.dataset.buttonLabel || "Get My Free Printables";
      helper.hidden = false;
      if (!status.hidden) status.hidden = true;
      if (status.textContent) status.textContent = "";
    });
  }

  function setSubmitting(active) {
    state.submitting = active;
    checkoutGroups().forEach(({ action }) => {
      const button = action.querySelector("[data-supplyme-free-order-submit]");
      if (!button) return;
      button.disabled = active;
      button.setAttribute("aria-busy", active ? "true" : "false");
      button.textContent = active
        ? (root.dataset.processingLabel || "Preparing Your Printables…")
        : (root.dataset.buttonLabel || "Get My Free Printables");
    });
  }

  function showError(message) {
    checkoutGroups().forEach(({ action }) => {
      if (action.hidden) return;
      const status = action.querySelector("[data-supplyme-free-order-status]");
      status.textContent = message;
      status.hidden = false;
    });
  }

  function eligibleForButton(context) {
    return context?.success === true &&
      context.eligible === true &&
      context.shadow_mode === false &&
      context.flow_enabled === true &&
      context.order_creation_enabled === true;
  }

  async function evaluate() {
    if (state.submitting) return;
    const sequence = ++state.sequence;

    try {
      const cart = await loadCart();
      if (sequence !== state.sequence) return;
      if (!cart.items.length) {
        state.lastFingerprint = "";
        state.lastContext = null;
        showCheckout();
        return;
      }

      const currentFingerprint = fingerprint(cart);
      if (currentFingerprint === state.lastFingerprint && state.lastContext) {
        eligibleForButton(state.lastContext) ? showFreeOrder() : showCheckout();
        return;
      }

      showChecking();
      const response = await fetch(root.dataset.contextUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ cart })
      });
      const context = await readJson(response);
      if (sequence !== state.sequence) return;

      state.lastFingerprint = currentFingerprint;
      state.lastContext = response.ok ? context : null;
      eligibleForButton(context) ? showFreeOrder() : showCheckout();
    } catch {
      if (sequence === state.sequence) {
        state.lastFingerprint = "";
        state.lastContext = null;
        showCheckout();
      }
    }
  }

  function scheduleEvaluate(delay = 140) {
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(evaluate, delay);
  }

  function storageKey(cartFingerprint) {
    let hash = 2166136261;
    for (let index = 0; index < cartFingerprint.length; index += 1) {
      hash ^= cartFingerprint.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `${STORAGE_PREFIX}${(hash >>> 0).toString(16)}`;
  }

  function idempotencyKey(cartFingerprint) {
    const key = storageKey(cartFingerprint);
    try {
      const existing = String(window.sessionStorage.getItem(key) || "");
      if (/^[A-Za-z0-9_-]{20,128}$/.test(existing)) return existing;
    } catch {}

    const created = window.crypto?.randomUUID?.() ||
      `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
    try { window.sessionStorage.setItem(key, created); } catch {}
    return created;
  }

  async function removeOrderedLines(lineKeys) {
    const updates = {};
    lineKeys.forEach((key) => { updates[key] = 0; });
    const response = await fetch(`${routesRoot()}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error("The order was created, but the cart could not be cleared.");
  }

  function successUrl(result) {
    const configured = String(root.dataset.successUrl || "").trim();
    const workerRedirect = String(result.redirect_url || "").trim();
    const url = new URL(
      workerRedirect || configured || root.dataset.cartUrl || "/cart",
      window.location.origin
    );
    url.searchParams.set("supplyme_order", String(result.order?.name || ""));
    url.searchParams.set("supplyme_request", String(result.request_key || ""));
    return url.toString();
  }

  async function submitOrder() {
    if (state.submitting) return;
    setSubmitting(true);

    try {
      const cart = await loadCart();
      const currentFingerprint = fingerprint(cart);
      const response = await fetch(root.dataset.createUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          idempotency_key: idempotencyKey(currentFingerprint),
          cart
        })
      });
      const result = await readJson(response);

      if (response.status === 202 && result.status === "processing") {
        throw new Error("Your order is still being prepared. Wait a moment, then select the button again.");
      }
      if (!response.ok || result.success !== true || result.status !== "completed") {
        if (result.error_code === "CART_NOT_ELIGIBLE") {
          state.lastFingerprint = "";
          state.lastContext = null;
          showCheckout();
          throw new Error("Your cart changed and now requires Shopify Checkout.");
        }
        throw new Error(result.error || root.dataset.errorText ||
          "We couldn’t prepare your printable order. Your cart has not been cleared. Please try again.");
      }

      const lineKeys = Array.isArray(result.cart_line_keys)
        ? result.cart_line_keys
        : cart.items.map((item) => item.key);
      await removeOrderedLines(lineKeys);
      window.location.assign(successUrl(result));
    } catch (error) {
      showError(error instanceof Error ? error.message :
        (root.dataset.errorText || "We couldn’t prepare your printable order. Please try again."));
      setSubmitting(false);
    }
  }

  const observer = new MutationObserver((mutations) => {
    const cartContentChanged = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE
        ? mutation.target
        : mutation.target.parentElement;
      return !target?.closest?.("[data-supplyme-free-order-action]");
    });
    if (cartContentChanged) scheduleEvaluate(80);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  CART_EVENTS.forEach((eventName) => document.addEventListener(eventName, () => {
    state.lastFingerprint = "";
    state.lastContext = null;
    scheduleEvaluate();
  }));
  window.addEventListener("pageshow", () => scheduleEvaluate(0));

  window.SupplyMeFreeOrder = Object.freeze({ evaluate, scheduleEvaluate });
  scheduleEvaluate(0);
})();
